/* SA EDIT — cart recovery
 *
 * Three jobs:
 *   1. Save the shopper's WhatsApp number into cart attributes so an abandoned
 *      checkout is recoverable at all.
 *   2. Log what happens when the checkout button is pressed, because Initiate
 *      Checkout has not fired in 12 days while Add to Cart still does. That gap
 *      points at a mechanical failure, and this is how you find it.
 *   3. Offer help on the way out of the cart, once per session.
 *
 * Diagnostics: append ?sadebug=1 to any URL to print state to the console.
 */

(function () {
  'use strict';

  var CONFIG = {
    whatsappNumber: '923289889888',
    exitPromptEnabled: true,
    exitPromptPaths: ['/cart'],
    debug: new URLSearchParams(window.location.search).has('sadebug'),
  };

  function log() {
    if (!CONFIG.debug) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[sa-cart]'].concat(args));
  }

  /* ------------------------------------------------------------------ *
   * 1. Contact capture -> cart attributes
   * ------------------------------------------------------------------ */

  function normalisePakistaniNumber(raw) {
    var digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.indexOf('92') === 0) return digits;
    if (digits.indexOf('0') === 0) return '92' + digits.slice(1);
    if (digits.length === 10 && digits.indexOf('3') === 0) return '92' + digits;
    return digits;
  }

  function isPlausibleNumber(normalised) {
    // Pakistani mobile numbers are 92 followed by 10 digits starting with 3.
    return /^923\d{9}$/.test(normalised);
  }

  function saveAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart update failed: ' + response.status);
      return response.json();
    });
  }

  function initCapture() {
    var roots = document.querySelectorAll('[data-sa-capture]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var input = root.querySelector('[data-sa-capture-input]');
      var status = root.querySelector('[data-sa-capture-status]');
      if (!input) return;

      var timer = null;
      var lastSaved = input.value;

      function setStatus(message, state) {
        if (!status) return;
        status.textContent = message;
        if (state) {
          status.setAttribute('data-state', state);
        } else {
          status.removeAttribute('data-state');
        }
      }

      function commit() {
        var normalised = normalisePakistaniNumber(input.value);
        var optin = root.querySelector('[data-sa-capture-optin]');

        if (!normalised) {
          setStatus('');
          return;
        }

        if (!isPlausibleNumber(normalised)) {
          setStatus('That does not look like a Pakistani mobile number yet.');
          return;
        }

        if (normalised === lastSaved) return;

        var attributes = { WhatsApp: normalised };
        if (optin) {
          attributes['WhatsApp Marketing Opt-In'] = optin.checked ? 'yes' : 'no';
        }

        saveAttributes(attributes)
          .then(function () {
            lastSaved = normalised;
            setStatus('Saved. We can reach you if anything comes up.', 'saved');
            log('attribute saved', normalised);
            try {
              sessionStorage.setItem('sa_wa', normalised);
            } catch (e) {
              /* private browsing */
            }
            document.querySelectorAll('[data-sa-capture-input]').forEach(function (other) {
              if (other !== input) other.value = input.value;
            });
          })
          .catch(function (error) {
            setStatus('We could not save that. Your order is unaffected.');
            log('attribute save failed', error);
          });
      }

      input.addEventListener('input', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(commit, 700);
      });

      input.addEventListener('blur', function () {
        window.clearTimeout(timer);
        commit();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Checkout button diagnostics
   *
   * Add to Cart fires but Initiate Checkout does not. Either the button never
   * submits, or it submits and the redirect fails. This tells you which.
   * ------------------------------------------------------------------ */

  var CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    '[data-sa-checkout]',
    'button[type="submit"][form="cart"]',
    '.cart__checkout-button',
    '#checkout',
  ].join(', ');

  function initCheckoutDiagnostics() {
    document.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest ? event.target.closest(CHECKOUT_SELECTORS) : null;
        if (!trigger) return;

        var startedAt = Date.now();
        var record = {
          at: new Date().toISOString(),
          href: window.location.pathname,
          disabled: trigger.disabled === true,
          inForm: Boolean(trigger.closest('form')),
          formAction: trigger.closest('form') ? trigger.closest('form').action : null,
          viewport: window.innerWidth + 'x' + window.innerHeight,
          userAgent: navigator.userAgent,
          navigated: false,
        };

        log('checkout pressed', record);

        // If nothing happens within 2.5s the button is not doing its job.
        window.setTimeout(function () {
          if (document.visibilityState === 'hidden') return; // we navigated away
          record.navigated = false;
          record.stalledMs = Date.now() - startedAt;
          console.warn('[sa-cart] Checkout did not navigate within 2.5s', record);
          try {
            var history = JSON.parse(sessionStorage.getItem('sa_checkout_log') || '[]');
            history.push(record);
            sessionStorage.setItem('sa_checkout_log', JSON.stringify(history.slice(-20)));
          } catch (e) {
            /* ignore */
          }
        }, 2500);
      },
      true
    );

    // Expose the log so you can read it from the console on a real device:
    //   copy(saCheckoutLog())
    window.saCheckoutLog = function () {
      try {
        return JSON.parse(sessionStorage.getItem('sa_checkout_log') || '[]');
      } catch (e) {
        return [];
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * 3. Leaving-the-cart prompt
   * ------------------------------------------------------------------ */

  function buildExitPrompt() {
    var overlay = document.createElement('div');
    overlay.className = 'sa-exit';
    overlay.hidden = true;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'sa-exit-title');

    var saved = '';
    try {
      saved = sessionStorage.getItem('sa_wa') || '';
    } catch (e) {
      /* ignore */
    }

    var message =
      'Hi SA EDIT, I have a few items in my cart and wanted to check sizing and delivery before I order.';

    overlay.innerHTML =
      '<div class="sa-exit__panel">' +
      '<h2 class="sa-exit__title" id="sa-exit-title">Not sure about the size?</h2>' +
      '<p class="sa-exit__body">Send us a message and we will help you pick the right fit. ' +
      'You can pay cash when the parcel arrives, and exchange within 7 days.</p>' +
      '<div class="sa-exit__actions">' +
      '<a class="sa-exit__primary" target="_blank" rel="noopener" href="https://wa.me/' +
      CONFIG.whatsappNumber +
      '?text=' +
      encodeURIComponent(message) +
      '">Ask on WhatsApp</a>' +
      '<button class="sa-exit__dismiss" type="button" data-sa-exit-dismiss>Keep shopping</button>' +
      '</div></div>';

    document.body.appendChild(overlay);
    log('exit prompt built', { savedNumber: Boolean(saved) });
    return overlay;
  }

  function initExitPrompt() {
    if (!CONFIG.exitPromptEnabled) return;

    var onCartPage = CONFIG.exitPromptPaths.some(function (path) {
      return window.location.pathname.indexOf(path) === 0;
    });
    if (!onCartPage) return;

    try {
      if (sessionStorage.getItem('sa_exit_shown') === '1') return;
    } catch (e) {
      /* ignore */
    }

    var overlay = null;
    var shown = false;

    function show() {
      if (shown) return;
      shown = true;
      overlay = overlay || buildExitPrompt();
      overlay.hidden = false;
      var dismiss = overlay.querySelector('[data-sa-exit-dismiss]');
      if (dismiss) dismiss.focus();
      try {
        sessionStorage.setItem('sa_exit_shown', '1');
      } catch (e) {
        /* ignore */
      }
    }

    function hide() {
      if (overlay) overlay.hidden = true;
    }

    // Desktop: cursor leaves through the top of the window.
    document.addEventListener('mouseout', function (event) {
      if (event.clientY <= 0 && !event.relatedTarget) show();
    });

    // Mobile: no exit intent exists, so use a dwell timer instead.
    if (window.matchMedia('(max-width: 749px)').matches) {
      window.setTimeout(show, 25000);
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-sa-exit-dismiss]')) hide();
      if (event.target.classList.contains('sa-exit')) hide();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hide();
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Mobile announcement bar rotation
   * ------------------------------------------------------------------ */

  function initBarRotation() {
    var items = document.querySelectorAll('.sa-bar__item');
    if (items.length < 2) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    var index = 0;
    items[0].classList.add('is-visible');

    window.setInterval(function () {
      items[index].classList.remove('is-visible');
      index = (index + 1) % items.length;
      items[index].classList.add('is-visible');
    }, 4000);
  }

  /* ------------------------------------------------------------------ */

  function init() {
    initCapture();
    initCheckoutDiagnostics();
    initExitPrompt();
    initBarRotation();
    log('ready', { path: window.location.pathname });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
